/**
 * Server-side structured data for the CompareLenderQuotes tool page.
 * Lives in a data module (not the page component) so routeMeta can emit it
 * into the server HTML; react-helmet would only add it after hydration.
 */
export const FAQS = [
  {
    topic: "how_many_lenders",
    q: "How many lenders should you compare?",
    a: "Three to five is the practical range. One quote gives you nothing to negotiate against, two rarely reveals whether a fee is unusual, and past five the gains usually shrink while the paperwork piles up. Three to five quotes on the same loan amount and term is enough to see the real spread in rates and origination charges.",
  },
  {
    topic: "does_shopping_hurt",
    q: "Will rate shopping hurt your credit?",
    a: "Usually far less than people expect. A single hard mortgage inquiry typically has a small effect — often under five points for most people, and less if your credit file is thick and well established. Credit scoring models also treat multiple mortgage inquiries made close together as one shopping event, so applying to several lenders in a short window is generally counted much like a single application.",
  },
  {
    topic: "credit_window",
    q: "Is the rate-shopping window 14 days or 45 days?",
    a: "It depends on which scoring model the lender uses. Newer FICO models may group mortgage inquiries over a 45-day window, and VantageScore uses a similar rolling window. Older FICO versions that some mortgage lenders still pull use a 14-day window. Because you don't control which model gets used, keep every mortgage application inside 14 days — that window is safe under all of them.",
  },
  {
    topic: "same_loan_type",
    q: "Does the grouping apply to any loan I apply for?",
    a: "No. Inquiry grouping applies to the same loan type. Mortgage inquiries group with other mortgage inquiries. If you open a credit card, finance a car, or take a personal loan during your mortgage shopping window, those are separate hard inquiries and they can also change the debt picture your lender underwrites.",
  },
  {
    topic: "soft_vs_hard",
    q: "What's the difference between a soft pull and a hard pull?",
    a: "A soft pull — checking your own credit, or a prequalification that only needs a few self-reported details — does not affect your score. A hard pull happens when you submit a full application and the lender pulls your report to underwrite it; that one shows on your report and can affect your score. Prequalification is often soft; preapproval almost always involves a hard pull.",
  },
  {
    topic: "apr_vs_rate",
    q: "Should I compare interest rate or APR?",
    a: "Compare both, and compare Loan Estimates side by side. The interest rate sets your monthly principal and interest. The APR folds most lender fees into a single annualized number, so it's the better apples-to-apples figure across quotes — but it assumes you keep the loan for the full term. If you expect to sell or refinance in a few years, the five-year cost and the break-even on any upfront points matter more than APR.",
  },
];

export const SHOPPER_CONCERNS = [
  {
    q: "\u201cMy score already sits near a cutoff. Should I skip shopping?\u201d",
    a: "No — that's the situation where a better quote is worth the most. Keep every application inside 14 days, avoid new accounts during that stretch, and consider prequalifying with soft pulls first to narrow the list before anyone runs a hard inquiry.",
  },
  {
    q: "\u201cI financed a car in the middle of shopping. What happens?\u201d",
    a: "The auto inquiry doesn't group with your mortgage inquiries, and the new monthly payment changes your debt-to-income ratio. That can shift what you qualify for. Tell your loan officer immediately so the numbers get reworked before closing.",
  },
  {
    q: "\u201cIs it rude to tell a lender I'm shopping around?\u201d",
    a: "Not at all — it's expected. Lenders compete on price. Sharing a competing Loan Estimate is the most reliable way to get origination charges reduced or a rate matched, and you should ask for any improvement in writing.",
  },
  {
    q: "\u201cOne quote has a much lower rate but big upfront points. Which wins?\u201d",
    a: "Run the break-even. Divide the extra upfront cost by the monthly savings to see the month the cheaper rate pulls ahead. If you expect to move or refinance before that month, the lower-fee quote is usually the better deal.",
  },
  {
    q: "\u201cHow do I know when to stop shopping?\u201d",
    a: "When three to five written Loan Estimates for the same loan cluster within a small range and no lender will improve their offer, you've found the market. Stop, lock your rate, and confirm the lock length in writing.",
  },
];

  export const COMPARE_LENDER_QUOTES_JSON_LD: Record<string, unknown> = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      name: "How Many Lenders Should You Compare and Will Rate Shopping Hurt Your Credit?",
      url: "https://www.homeapproach.com/tools/compare-lender-quotes",
      description:
        "Compare 3 to 5 mortgage quotes side by side and see how rate shopping actually affects your credit score.",
      isPartOf: {
        "@type": "WebSite",
        name: "Home Approach",
        url: "https://www.homeapproach.com",
      },
      publisher: {
        "@type": "Organization",
        name: "Home Approach",
        url: "https://www.homeapproach.com",
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.homeapproach.com/" },
        { "@type": "ListItem", position: 2, name: "Tools", item: "https://www.homeapproach.com/tools" },
        {
          "@type": "ListItem",
          position: 3,
          name: "Compare Lender Quotes",
          item: "https://www.homeapproach.com/tools/compare-lender-quotes",
        },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: [...FAQS, ...SHOPPER_CONCERNS].map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};
